import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Fade,
  Collapse,
  Chip,
  Switch,
  Paper,
} from '@mui/material';
import { 
  ContentPaste, 
  AutoFixHigh,
  AccountTree,
  Link as LinkIcon,
  Code,
  DataObject,
  CheckCircle,
  Gavel,
  Security,
  VerifiedUser,
  Policy,
  Description,
  Terminal,
  Send,
  PlayArrow,
} from '@mui/icons-material';

interface CurlWhirlResult {
  url: string;
  method: string;
  headers: Record<string, string>;
  headersJson: string;
  body: string;
  bodyType: 'json' | 'xml' | 'form' | 'text' | 'binary' | 'multipart';
}

interface VariableSubstitution {
  location: string;
  field: string;
  current_value: string;
  suggested_variable: string;
}

interface SmartSuggestions {
  step_name?: string;
  base_url?: string;
  extracted_variables?: Array<{
    name: string;
    path: string;
    value?: string;
    description?: string;
  }>;
  variable_substitutions?: VariableSubstitution[];
  skip_condition?: {
    variable: string;
    operator: string;
    value: string;
    description?: string;
  };
  bias_analysis?: {
    enabled: boolean;
    text_path?: string;
    text_preview?: string;
    description?: string;
  };
  rag_analysis?: {
    enabled: boolean;
    query_path?: string;
    context_path?: string;
    response_path?: string;
    query_preview?: string;
    context_preview?: string;
    response_preview?: string;
    description?: string;
  };
}

interface SmartCurlResult extends CurlWhirlResult {
  executed?: boolean;
  response?: {
    status_code: number;
    headers: Record<string, string>;
    body: any;
    body_preview?: string;
  };
  suggestions?: SmartSuggestions;
  execution_error?: string;
}

interface ParsedCurl {
  url: string;
  method: string;
  headers: Record<string, string>;
  body: string;
  bodyType: string;
}

interface CurlWhirlModalProps {
  open: boolean;
  onClose: () => void;
  onApply: (result: CurlWhirlResult, suggestions?: SmartSuggestions) => void;
  previousVariables?: Record<string, any>;
  sequenceId?: string;  // For loading RAG context files
}

// Simple client-side curl parser
const parseCurlLocally = (curlCommand: string): ParsedCurl | null => {
  if (!curlCommand.trim().toLowerCase().startsWith('curl')) {
    return null;
  }

  const result: ParsedCurl = {
    url: '',
    method: 'GET',
    headers: {},
    body: '',
    bodyType: 'json',
  };

  try {
    const normalized = curlCommand
      .replace(/\\\s*\n/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    const urlMatch = normalized.match(/curl\s+['"]([^'"]+)['"]/i) || 
                     normalized.match(/curl\s+([^\s-][^\s]*)/i);
    if (urlMatch) {
      result.url = urlMatch[1];
    }

    const methodMatch = normalized.match(/-X\s+['"]?(\w+)['"]?/i) ||
                        normalized.match(/--request\s+['"]?(\w+)['"]?/i);
    if (methodMatch) {
      result.method = methodMatch[1].toUpperCase();
    }

    const headerRegexSingle = /-H\s+'([^']+)'/gi;
    let headerMatch;
    while ((headerMatch = headerRegexSingle.exec(normalized)) !== null) {
      const headerStr = headerMatch[1];
      const colonIndex = headerStr.indexOf(':');
      if (colonIndex > 0) {
        const key = headerStr.substring(0, colonIndex).trim();
        const value = headerStr.substring(colonIndex + 1).trim();
        result.headers[key] = value;
      }
    }
    
    const headerRegexDouble = /-H\s+"([^"]+)"/gi;
    while ((headerMatch = headerRegexDouble.exec(normalized)) !== null) {
      const headerStr = headerMatch[1];
      const colonIndex = headerStr.indexOf(':');
      if (colonIndex > 0) {
        const key = headerStr.substring(0, colonIndex).trim();
        const value = headerStr.substring(colonIndex + 1).trim();
        result.headers[key] = value;
      }
    }

    const cookieMatch = normalized.match(/-b\s+'([^']+)'/i) ||
                        normalized.match(/-b\s+"([^"]+)"/i) ||
                        normalized.match(/--cookie\s+'([^']+)'/i) ||
                        normalized.match(/--cookie\s+"([^"]+)"/i);
    if (cookieMatch) {
      result.headers['Cookie'] = cookieMatch[1];
    }

    let dataMatch = normalized.match(/(?:--data-raw|--data-binary|--data|-d)\s+'((?:[^'\\]|\\.)*)'/i);
    if (!dataMatch) {
      dataMatch = normalized.match(/(?:--data-raw|--data-binary|--data|-d)\s+"((?:[^"\\]|\\.)*)"/i);
    }
    
    if (dataMatch) {
      result.body = dataMatch[1];
      if (!methodMatch) {
        result.method = 'POST';
      }
    }

    const contentTypeKey = Object.keys(result.headers).find(k => k.toLowerCase() === 'content-type');
    const contentType = contentTypeKey ? result.headers[contentTypeKey] : '';
    
    if (contentType.includes('json')) {
      result.bodyType = 'json';
    } else if (contentType.includes('xml')) {
      result.bodyType = 'xml';
    } else if (contentType.includes('form-urlencoded')) {
      result.bodyType = 'form';
    } else if (contentType.includes('multipart')) {
      result.bodyType = 'multipart';
    } else {
      result.bodyType = 'json';
    }

    return result;
  } catch (e) {
    console.error('Error parsing curl:', e);
    return null;
  }
};

// Professional color palette for legal AI software
const legalColors = {
  primary: '#1e3a5f',      // Deep navy
  secondary: '#2d4a6f',    // Slate blue
  accent: '#0d9488',       // Teal accent
  success: '#059669',      // Professional green
  warning: '#d97706',      // Amber
  error: '#dc2626',        // Red
  background: '#0f172a',   // Dark slate
  surface: '#1e293b',      // Elevated surface
  border: '#334155',       // Border color
  text: '#f1f5f9',         // Light text
  textMuted: '#94a3b8',    // Muted text
};

// Tree node component with professional styling
const TreeNode: React.FC<{
  label: string;
  value?: string;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  defaultExpanded?: boolean;
  color?: string;
}> = ({ label, value, icon, children, defaultExpanded = true, color = legalColors.accent }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  
  return (
    <Box sx={{ ml: children ? 0 : 2 }}>
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'flex-start', 
          gap: 1, 
          py: 0.75,
          px: 1,
          cursor: children ? 'pointer' : 'default',
          borderRadius: 1,
          transition: 'background-color 0.15s ease',
          '&:hover': children ? { bgcolor: 'rgba(13, 148, 136, 0.08)' } : {},
        }}
        onClick={() => children && setExpanded(!expanded)}
      >
        {children && (
          <Typography sx={{ color: legalColors.textMuted, fontFamily: 'monospace', minWidth: 16, fontSize: '0.75rem' }}>
            {expanded ? '▾' : '▸'}
          </Typography>
        )}
        {icon && <Box sx={{ color, mt: 0.2, opacity: 0.9 }}>{icon}</Box>}
        <Typography variant="body2" sx={{ color, fontWeight: 600, minWidth: 80, letterSpacing: '0.025em' }}>
          {label}
        </Typography>
        {value && (
          <Typography 
            variant="body2" 
            sx={{ 
              color: legalColors.text, 
              fontFamily: '"SF Mono", "Monaco", "Inconsolata", monospace', 
              fontSize: '0.8rem',
              wordBreak: 'break-all',
              flex: 1,
            }}
          >
            {value}
          </Typography>
        )}
      </Box>
      {children && (
        <Collapse in={expanded}>
          <Box sx={{ ml: 3, borderLeft: `2px solid ${legalColors.border}`, pl: 1.5 }}>
            {children}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

export const CurlWhirlModal: React.FC<CurlWhirlModalProps> = ({
  open,
  onClose,
  onApply,
  previousVariables = {},
  sequenceId,
}) => {
  const [curlCommand, setCurlCommand] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SmartCurlResult | null>(null);
  const [localParsed, setLocalParsed] = useState<ParsedCurl | null>(null);
  const [smartMode, setSmartMode] = useState(true); // Default to ON

  useEffect(() => {
    if (curlCommand.trim()) {
      const parsed = parseCurlLocally(curlCommand);
      setLocalParsed(parsed);
    } else {
      setLocalParsed(null);
    }
  }, [curlCommand]);

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setCurlCommand(text);
      setError(null);
      setResult(null);
    } catch (err) {
      setError('Failed to read from clipboard. Please paste manually.');
    }
  };

  const handleParse = async () => {
    if (!curlCommand.trim()) {
      setError('Please enter a curl command');
      return;
    }

    if (!curlCommand.trim().toLowerCase().startsWith('curl')) {
      setError('Command must start with "curl"');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (smartMode) {
        // Use GraphQL for smart mode
        const graphqlQuery = `
          mutation SmartCurlParse($curlCommand: String!, $previousVariables: JSON, $sequenceId: ID) {
            smartCurlParse(curlCommand: $curlCommand, previousVariables: $previousVariables, sequenceId: $sequenceId) {
              parsed {
                url
                method
                headers
                headersJson
                body
                bodyType
              }
              executed
              response {
                statusCode
                headers
                body
                bodyPreview
              }
              suggestions {
                stepName
                baseUrl
                extractedVariables {
                  name
                  path
                  value
                  description
                }
                variableSubstitutions {
                  location
                  field
                  currentValue
                  suggestedVariable
                }
                biasAnalysis {
                  enabled
                  textPath
                  textPreview
                }
                ragAnalysis {
                  enabled
                  queryPath
                  contextPath
                  responsePath
                  queryPreview
                  contextPreview
                  responsePreview
                }
                skipCondition {
                  variable
                  operator
                  value
                  description
                }
              }
              executionError
            }
          }
        `;
        
        const response = await fetch('/graphql/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            query: graphqlQuery,
            variables: {
              curlCommand: curlCommand,
              previousVariables: previousVariables,
              sequenceId: sequenceId,
            },
          }),
          credentials: 'include',
        });

        const data = await response.json();

        if (data.errors) {
          throw new Error(data.errors[0]?.message || 'GraphQL error');
        }

        const result = data.data?.smartCurlParse;
        if (!result) {
          throw new Error('No result returned from smart curl parse');
        }

        // Transform GraphQL response to match expected format
        setResult({
          url: result.parsed.url,
          method: result.parsed.method,
          headers: result.parsed.headers,
          headersJson: result.parsed.headersJson,
          body: result.parsed.body,
          bodyType: result.parsed.bodyType as any,
          executed: result.executed,
          response: result.response ? {
            status_code: result.response.statusCode,
            headers: result.response.headers,
            body: result.response.body,
            body_preview: result.response.bodyPreview,
          } : undefined,
          suggestions: result.suggestions ? {
            step_name: result.suggestions.stepName,
            base_url: result.suggestions.baseUrl,
            extracted_variables: result.suggestions.extractedVariables?.map((v: any) => ({
              name: v.name,
              path: v.path,
              value: v.value,
              description: v.description,
            })),
            variable_substitutions: result.suggestions.variableSubstitutions?.map((v: any) => ({
              location: v.location,
              field: v.field,
              current_value: v.currentValue,
              suggested_variable: v.suggestedVariable,
            })),
            bias_analysis: result.suggestions.biasAnalysis ? {
              enabled: result.suggestions.biasAnalysis.enabled,
              text_path: result.suggestions.biasAnalysis.textPath,
              text_preview: result.suggestions.biasAnalysis.textPreview,
            } : undefined,
            rag_analysis: result.suggestions.ragAnalysis ? {
              enabled: result.suggestions.ragAnalysis.enabled,
              query_path: result.suggestions.ragAnalysis.queryPath,
              context_path: result.suggestions.ragAnalysis.contextPath,
              response_path: result.suggestions.ragAnalysis.responsePath,
              query_preview: result.suggestions.ragAnalysis.queryPreview,
              context_preview: result.suggestions.ragAnalysis.contextPreview,
              response_preview: result.suggestions.ragAnalysis.responsePreview,
            } : undefined,
            skip_condition: result.suggestions.skipCondition ? {
              variable: result.suggestions.skipCondition.variable,
              operator: result.suggestions.skipCondition.operator,
              value: result.suggestions.skipCondition.value,
              description: result.suggestions.skipCondition.description,
            } : undefined,
          } : undefined,
          execution_error: result.executionError,
        });
      } else {
        // Use REST API for non-smart mode (basic parsing)
        const response = await fetch('/api/v1/sequences/parse-curl/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ curl_command: curlCommand }),
          credentials: 'include',
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to parse curl command');
        }

        setResult({
          url: data.url,
          method: data.method,
          headers: data.headers,
          headersJson: data.headersJson,
          body: data.body,
          bodyType: data.bodyType,
        });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to parse curl command');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    if (result) {
      const curlResult: CurlWhirlResult = {
        url: result.url,
        method: result.method,
        headers: result.headers,
        headersJson: result.headersJson,
        body: result.body,
        bodyType: result.bodyType,
      };
      onApply(curlResult, result.suggestions);
      handleClose();
    }
  };

  const handleClose = () => {
    setCurlCommand('');
    setError(null);
    setResult(null);
    setLocalParsed(null);
    setLoading(false);
    onClose();
  };

  const getMethodColor = (method: string) => {
    const colors: Record<string, string> = {
      GET: '#1e3a5f',
      POST: '#3b82f6',
      PUT: '#0891b2',
      PATCH: '#1e3a5f',
      DELETE: '#ef4444',
    };
    return colors[method?.toUpperCase()] || '#6b7280';
  };

  const formatBody = (body: string): string => {
    try {
      const parsed = JSON.parse(body);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return body;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          background: legalColors.background,
          border: `1px solid ${legalColors.border}`,
          borderRadius: 2,
          minHeight: '80vh',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          background: legalColors.surface,
          borderBottom: `1px solid ${legalColors.border}`,
          py: 2.5,
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          width: 44, 
          height: 44, 
          borderRadius: 1.5,
          background: `linear-gradient(135deg, ${legalColors.primary} 0%, ${legalColors.secondary} 100%)`,
        }}>
          <Terminal sx={{ color: legalColors.text, fontSize: 24 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: legalColors.text, letterSpacing: '-0.025em' }}>
            API Request Configuration
          </Typography>
          <Typography variant="body2" sx={{ color: legalColors.textMuted, mt: 0.25 }}>
            Import and configure HTTP requests for compliance monitoring
          </Typography>
        </Box>
        
        {/* Smart Analysis Toggle */}
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1,
            borderRadius: 1.5,
            bgcolor: smartMode ? 'rgba(13, 148, 136, 0.1)' : 'rgba(148, 163, 184, 0.1)',
            border: `1px solid ${smartMode ? legalColors.accent : legalColors.border}`,
            transition: 'all 0.2s ease',
          }}
        >
          <VerifiedUser sx={{ fontSize: 18, color: smartMode ? legalColors.accent : legalColors.textMuted }} />
          <Typography variant="body2" sx={{ color: smartMode ? legalColors.accent : legalColors.textMuted, fontWeight: 500 }}>
            Smart Analysis
          </Typography>
          <Switch
            checked={smartMode}
            onChange={(e) => setSmartMode(e.target.checked)}
            size="small"
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: legalColors.accent,
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: legalColors.accent,
              },
            }}
          />
        </Paper>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, display: 'flex', gap: 3, bgcolor: legalColors.background }}>
        {/* Left Side - Curl Input */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Section Header */}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Description sx={{ fontSize: 18, color: legalColors.accent }} />
              <Typography variant="subtitle2" sx={{ color: legalColors.text, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.7rem' }}>
                Request Input
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<ContentPaste />}
                onClick={handlePaste}
                size="small"
                sx={{ 
                  borderColor: legalColors.border,
                  color: legalColors.text,
                  '&:hover': {
                    borderColor: legalColors.accent,
                    bgcolor: 'rgba(13, 148, 136, 0.08)',
                  }
                }}
              >
                Paste from Clipboard
              </Button>
              {smartMode && (
                <Chip 
                  icon={<PlayArrow sx={{ fontSize: 14 }} />}
                  label="Execute & Analyze" 
                  size="small" 
                  sx={{ 
                    bgcolor: 'rgba(13, 148, 136, 0.15)', 
                    color: legalColors.accent,
                    border: `1px solid rgba(13, 148, 136, 0.3)`,
                    '& .MuiChip-icon': { color: legalColors.accent },
                    fontWeight: 500,
                  }} 
                />
              )}
            </Box>
          </Box>

          <TextField
            fullWidth
            multiline
            rows={12}
            value={curlCommand}
            onChange={(e) => {
              setCurlCommand(e.target.value);
              setError(null);
              setResult(null);
            }}
            placeholder={`Paste your cURL command here...

Example:
curl 'https://api.example.com/graphql' \\
  -H 'Content-Type: application/json' \\
  -H 'Authorization: Bearer token123' \\
  --data-raw '{"query": "{ users { id name } }"}'`}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontFamily: '"SF Mono", "Monaco", "Inconsolata", monospace',
                fontSize: '0.85rem',
                backgroundColor: legalColors.surface,
                color: legalColors.text,
                borderRadius: 1.5,
                '& fieldset': {
                  borderColor: legalColors.border,
                },
                '&:hover fieldset': {
                  borderColor: legalColors.accent,
                },
                '&.Mui-focused fieldset': {
                  borderColor: legalColors.accent,
                  borderWidth: 2,
                },
              },
              '& .MuiInputBase-input': {
                color: legalColors.text,
              },
              '& .MuiInputBase-input::placeholder': {
                color: legalColors.textMuted,
                opacity: 0.7,
              },
            }}
          />

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mt: 2,
                bgcolor: 'rgba(220, 38, 38, 0.1)',
                border: `1px solid rgba(220, 38, 38, 0.3)`,
                color: legalColors.text,
                '& .MuiAlert-icon': { color: legalColors.error }
              }}
            >
              {error}
            </Alert>
          )}

          {result && (
            <Fade in>
              <Box sx={{ mt: 2 }}>
                <Alert 
                  severity="success"
                  icon={<CheckCircle sx={{ color: legalColors.success }} />}
                  sx={{ 
                    bgcolor: 'rgba(5, 150, 105, 0.1)',
                    border: `1px solid rgba(5, 150, 105, 0.3)`,
                    color: legalColors.text,
                  }}
                >
                  Request parsed successfully{result.executed && ' and executed'}
                </Alert>
                
                {/* Smart Mode Results */}
                {smartMode && result.suggestions && (
                  <Paper 
                    elevation={0}
                    sx={{ 
                      mt: 2, 
                      p: 2, 
                      bgcolor: legalColors.surface, 
                      borderRadius: 1.5, 
                      border: `1px solid ${legalColors.border}`, 
                      maxHeight: 300, 
                      overflow: 'auto' 
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, pb: 1, borderBottom: `1px solid ${legalColors.border}` }}>
                      <Security sx={{ fontSize: 18, color: legalColors.accent }} />
                      <Typography variant="subtitle2" sx={{ color: legalColors.accent, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.7rem' }}>
                        Analysis Results
                      </Typography>
                    </Box>
                    
                    {/* Response Status */}
                    {result.response && (
                      <Box sx={{ mb: 1.5, display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ color: legalColors.textMuted, fontWeight: 500 }}>Response Status:</Typography>
                        <Chip 
                          label={`HTTP ${result.response.status_code}`} 
                          size="small"
                          sx={{ 
                            bgcolor: result.response.status_code < 400 ? 'rgba(5, 150, 105, 0.2)' : 'rgba(220, 38, 38, 0.2)',
                            color: result.response.status_code < 400 ? legalColors.success : legalColors.error,
                            border: `1px solid ${result.response.status_code < 400 ? 'rgba(5, 150, 105, 0.4)' : 'rgba(220, 38, 38, 0.4)'}`,
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                    )}
                    
                    {/* Extracted Variables */}
                    {result.suggestions.extracted_variables && result.suggestions.extracted_variables.length > 0 && (
                      <Box sx={{ mb: 1.5 }}>
                        <Typography variant="caption" sx={{ color: legalColors.textMuted, fontWeight: 600, display: 'block', mb: 0.5 }}>
                          Extracted Variables ({result.suggestions.extracted_variables.length})
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, ml: 1 }}>
                          {result.suggestions.extracted_variables.slice(0, 8).map((v, i) => (
                            <Typography key={i} variant="caption" sx={{ color: legalColors.text, fontFamily: '"SF Mono", monospace', fontSize: '0.7rem' }}>
                              <span style={{ color: legalColors.accent }}>{v.name}</span>
                              <span style={{ color: legalColors.textMuted }}> ← </span>
                              <span style={{ color: '#60a5fa' }}>{v.path}</span>
                              {v.description && <span style={{ color: legalColors.textMuted }}> ({v.description})</span>}
                            </Typography>
                          ))}
                          {result.suggestions.extracted_variables.length > 8 && (
                            <Typography variant="caption" sx={{ color: legalColors.textMuted, fontStyle: 'italic' }}>
                              +{result.suggestions.extracted_variables.length - 8} more
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}
                    
                    {/* Variable Substitutions */}
                    {result.suggestions.variable_substitutions && result.suggestions.variable_substitutions.length > 0 && (
                      <Box sx={{ mb: 1.5 }}>
                        <Typography variant="caption" sx={{ color: legalColors.textMuted, fontWeight: 600, display: 'block', mb: 0.5 }}>
                          Suggested Substitutions
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, ml: 1 }}>
                          {result.suggestions.variable_substitutions.map((sub, i) => (
                            <Typography key={i} variant="caption" sx={{ color: legalColors.text, fontFamily: '"SF Mono", monospace', fontSize: '0.7rem' }}>
                              <span style={{ color: legalColors.warning }}>{sub.location}.{sub.field}</span>
                              <span style={{ color: legalColors.textMuted }}> → </span>
                              <span style={{ color: legalColors.accent }}>{sub.suggested_variable}</span>
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    )}
                    
                    {/* Skip Condition */}
                    {result.suggestions.skip_condition && (
                      <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircle sx={{ color: legalColors.accent, fontSize: 14 }} />
                        <Typography variant="caption" sx={{ color: legalColors.text }}>
                          <strong>Skip Condition:</strong> {result.suggestions.skip_condition.variable} {result.suggestions.skip_condition.operator} {result.suggestions.skip_condition.value || '(empty)'}
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Bias Analysis */}
                    {result.suggestions.bias_analysis?.enabled && (
                      <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Policy sx={{ color: legalColors.accent, fontSize: 14 }} />
                        <Typography variant="caption" sx={{ color: legalColors.text }}>
                          <strong>Risk Analysis Path:</strong> <code style={{ color: legalColors.accent }}>{result.suggestions.bias_analysis.text_path}</code>
                        </Typography>
                      </Box>
                    )}
                    
                    {/* RAG Analysis */}
                    {result.suggestions.rag_analysis?.enabled && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" sx={{ color: legalColors.textMuted, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                          <Gavel sx={{ fontSize: 14 }} /> RAG Quality Analysis
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, ml: 1 }}>
                          <Typography variant="caption" sx={{ color: legalColors.text, fontSize: '0.7rem' }}>
                            <span style={{ color: legalColors.textMuted }}>Query:</span>{' '}
                            {result.suggestions.rag_analysis.query_preview 
                              ? `"${result.suggestions.rag_analysis.query_preview.substring(0, 60)}${result.suggestions.rag_analysis.query_preview.length > 60 ? '...' : ''}"`
                              : <span style={{ color: legalColors.textMuted }}>(auto-generated)</span>
                            }
                          </Typography>
                          <Typography variant="caption" sx={{ color: legalColors.text, fontSize: '0.7rem' }}>
                            <span style={{ color: legalColors.textMuted }}>Context:</span>{' '}
                            <code style={{ color: '#60a5fa' }}>{result.suggestions.rag_analysis.context_path}</code>
                          </Typography>
                          <Typography variant="caption" sx={{ color: legalColors.text, fontSize: '0.7rem' }}>
                            <span style={{ color: legalColors.textMuted }}>Response:</span>{' '}
                            <code style={{ color: legalColors.success }}>{result.suggestions.rag_analysis.response_path}</code>
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Paper>
                )}
              </Box>
            </Fade>
          )}
        </Box>

        {/* Right Side - Preview & Response */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Request Preview */}
          <Paper 
            elevation={0}
            sx={{ 
              flex: 1,
              bgcolor: legalColors.surface, 
              borderRadius: 1.5, 
              border: `1px solid ${legalColors.border}`,
              p: 2,
              overflow: 'auto',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, pb: 1, borderBottom: `1px solid ${legalColors.border}` }}>
              <AccountTree sx={{ color: legalColors.accent, fontSize: 18 }} />
              <Typography variant="subtitle2" sx={{ color: legalColors.text, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.7rem' }}>
                Request Structure
              </Typography>
            </Box>

            {!localParsed && (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                height: '60%',
                color: legalColors.textMuted,
              }}>
                <Terminal sx={{ fontSize: 48, mb: 2, opacity: 0.2 }} />
                <Typography variant="body2" sx={{ textAlign: 'center', color: legalColors.textMuted }}>
                  Paste a cURL command to preview<br />the request configuration
                </Typography>
              </Box>
            )}

            {localParsed && (
              <Box sx={{ fontFamily: '"SF Mono", monospace' }}>
                <TreeNode label="Method" icon={<Code sx={{ fontSize: 16 }} />} color={legalColors.accent}>
                  <Chip 
                    label={localParsed.method} 
                    size="small" 
                    sx={{ 
                      bgcolor: getMethodColor(localParsed.method),
                      color: 'white',
                      fontWeight: 600,
                      fontFamily: '"SF Mono", monospace',
                      letterSpacing: '0.05em',
                    }} 
                  />
                </TreeNode>

                <TreeNode label="Endpoint" icon={<LinkIcon sx={{ fontSize: 16 }} />} color={legalColors.accent}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: legalColors.text, 
                      fontFamily: '"SF Mono", monospace', 
                      fontSize: '0.75rem',
                      wordBreak: 'break-all',
                      bgcolor: 'rgba(13, 148, 136, 0.08)',
                      p: 1,
                      borderRadius: 1,
                      border: `1px solid rgba(13, 148, 136, 0.2)`,
                    }}
                  >
                    {localParsed.url || '(no URL detected)'}
                  </Typography>
                </TreeNode>

                <TreeNode 
                  label={`Headers (${Object.keys(localParsed.headers || {}).length})`}
                  icon={<DataObject sx={{ fontSize: 16 }} />}
                  color={legalColors.accent}
                  defaultExpanded={Object.keys(localParsed.headers || {}).length <= 5}
                >
                  {Object.keys(localParsed.headers || {}).length === 0 ? (
                    <Typography variant="body2" sx={{ color: legalColors.textMuted, fontStyle: 'italic', fontSize: '0.75rem' }}>
                      No headers detected
                    </Typography>
                  ) : (
                    Object.entries(localParsed.headers).slice(0, 10).map(([key, value]) => (
                      <Box key={key} sx={{ py: 0.25 }}>
                        <Typography component="span" sx={{ color: legalColors.accent, fontFamily: '"SF Mono", monospace', fontSize: '0.7rem' }}>
                          {key}:
                        </Typography>
                        <Typography component="span" sx={{ color: legalColors.text, fontFamily: '"SF Mono", monospace', fontSize: '0.7rem', ml: 1 }}>
                          {String(value).length > 40 ? `${String(value).substring(0, 40)}...` : value}
                        </Typography>
                      </Box>
                    ))
                  )}
                  {Object.keys(localParsed.headers).length > 10 && (
                    <Typography sx={{ color: legalColors.textMuted, fontSize: '0.7rem', fontStyle: 'italic' }}>
                      +{Object.keys(localParsed.headers).length - 10} more
                    </Typography>
                  )}
                </TreeNode>

                <TreeNode label="Content Type" icon={<Code sx={{ fontSize: 16 }} />} color={legalColors.accent}>
                  <Chip 
                    label={localParsed.bodyType.toUpperCase()} 
                    size="small" 
                    variant="outlined"
                    sx={{ 
                      borderColor: legalColors.border, 
                      color: legalColors.text, 
                      fontFamily: '"SF Mono", monospace',
                      fontWeight: 500,
                    }} 
                  />
                </TreeNode>

                {localParsed.body && (
                  <TreeNode 
                    label={`Request Body (${localParsed.body.length} chars)`}
                    icon={<DataObject sx={{ fontSize: 16 }} />}
                    color={legalColors.accent}
                  >
                    <Box sx={{ bgcolor: legalColors.background, p: 1.5, borderRadius: 1, maxHeight: 100, overflow: 'auto', border: `1px solid ${legalColors.border}` }}>
                      <Typography component="pre" sx={{ color: legalColors.text, fontFamily: '"SF Mono", monospace', fontSize: '0.65rem', whiteSpace: 'pre-wrap', m: 0 }}>
                        {formatBody(localParsed.body).substring(0, 500)}
                        {localParsed.body.length > 500 && '...'}
                      </Typography>
                    </Box>
                  </TreeNode>
                )}
              </Box>
            )}
          </Paper>

          {/* Response Preview (Smart Mode) */}
          {smartMode && result?.response && (
            <Paper 
              elevation={0}
              sx={{ 
                flex: 1,
                bgcolor: legalColors.surface, 
                borderRadius: 1.5, 
                border: `1px solid ${legalColors.border}`,
                p: 2,
                overflow: 'auto',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, pb: 1, borderBottom: `1px solid ${legalColors.border}` }}>
                <Send sx={{ color: legalColors.accent, fontSize: 18 }} />
                <Typography variant="subtitle2" sx={{ color: legalColors.text, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.7rem' }}>
                  Response Preview
                </Typography>
                <Chip 
                  label={`${result.response.status_code}`} 
                  size="small"
                  sx={{ 
                    bgcolor: result.response.status_code < 400 ? 'rgba(5, 150, 105, 0.2)' : 'rgba(220, 38, 38, 0.2)',
                    color: result.response.status_code < 400 ? legalColors.success : legalColors.error,
                    border: `1px solid ${result.response.status_code < 400 ? 'rgba(5, 150, 105, 0.4)' : 'rgba(220, 38, 38, 0.4)'}`,
                    fontWeight: 600,
                    ml: 'auto'
                  }}
                />
              </Box>
              <Box sx={{ bgcolor: legalColors.background, p: 1.5, borderRadius: 1, maxHeight: 200, overflow: 'auto', border: `1px solid ${legalColors.border}` }}>
                <Typography component="pre" sx={{ color: legalColors.text, fontFamily: '"SF Mono", monospace', fontSize: '0.65rem', whiteSpace: 'pre-wrap', m: 0 }}>
                  {result.response.body_preview || JSON.stringify(result.response.body, null, 2)?.substring(0, 1500)}
                </Typography>
              </Box>
            </Paper>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, borderTop: `1px solid ${legalColors.border}`, bgcolor: legalColors.surface }}>
        <Button 
          onClick={handleClose} 
          sx={{ 
            color: legalColors.textMuted,
            '&:hover': {
              color: legalColors.text,
              bgcolor: 'rgba(148, 163, 184, 0.1)',
            }
          }}
        >
          Cancel
        </Button>
        
        {!result ? (
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : (smartMode ? <VerifiedUser /> : <AutoFixHigh />)}
            onClick={handleParse}
            disabled={loading || !curlCommand.trim()}
            sx={{
              bgcolor: legalColors.accent,
              color: 'white',
              fontWeight: 600,
              px: 3,
              '&:hover': {
                bgcolor: '#0f766e',
              },
              '&:disabled': {
                bgcolor: legalColors.border,
                color: legalColors.textMuted,
              }
            }}
          >
            {loading ? 'Processing...' : (smartMode ? 'Analyze & Configure' : 'Parse Request')}
          </Button>
        ) : (
          <Button
            variant="contained"
            startIcon={<CheckCircle />}
            onClick={handleApply}
            sx={{
              bgcolor: legalColors.success,
              color: 'white',
              fontWeight: 600,
              px: 3,
              '&:hover': {
                bgcolor: '#047857',
              },
            }}
          >
            Apply Configuration {result.suggestions?.rag_analysis?.enabled && '+ RAG'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CurlWhirlModal;
